import { Helmet } from 'react-helmet-async';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Check, Clock, Lock, Shield, Loader2 } from 'lucide-react';
import DashboardLayout from '@/components/layout/DashboardLayout';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useEffect, useState } from 'react';
import apiService from '@/services/api';

const FundsAddedSuccessPage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [newBalance, setNewBalance] = useState<string>('£0.00');
  const [addedAmount, setAddedAmount] = useState<string>('£0.00');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchWalletBalance = async () => {
      try {
        const amount = searchParams.get('amount');
        if (amount) {
          setAddedAmount(`£${parseFloat(amount).toFixed(2)}`);
        }

        // Fetch current wallet balance
        const response = await apiService.request('/customer/wallet', 'GET');
        if (response.success && response.data) {
          // Handle both balance_pence and balance fields from the API
          let balance = 0;
          if (response.data.balance_pence !== undefined) {
            balance = response.data.balance_pence / 100;
          } else if (response.data.balance !== undefined) {
            balance = response.data.balance;
          } else if (response.data.availableBalance !== undefined) {
            balance = response.data.availableBalance;
          }
          setNewBalance(`£${Number(balance).toFixed(2)}`);
        }
      } catch (error) {
        console.error('Failed to fetch wallet balance:', error);
        setNewBalance('£0.00');
      } finally {
        setLoading(false);
      }
    };

    fetchWalletBalance();
  }, [searchParams]);

  const successDetails = {
    newBalance: newBalance,
    addedAmount: addedAmount,
    transactionId: `#VW-${Math.random().toString(36).substr(2, 9).toUpperCase()}-TRX`,
    processingTime: 'Instant',
    security: '256-bit Encrypted',
  };

  return (
    <DashboardLayout>
      <Helmet>
        <title>Funds Added Successfully | Virewire</title>
      </Helmet>

      {loading ? (
        <div className="flex items-center justify-center min-h-[70vh]">
          <div className="text-center">
            <Loader2 className="h-8 w-8 animate-spin text-green-700 mx-auto mb-4" />
            <p className="text-gray-600">Updating your wallet...</p>
          </div>
        </div>
      ) : (
        <div className="flex items-center justify-center min-h-[70vh]">
          <div className="max-w-2xl w-full">
            <div className="text-center mb-12">
              {/* Success Checkmark */}
              <div className="flex justify-center mb-6">
                <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center">
                  <Check className="h-12 w-12 text-green-600" />
                </div>
              </div>

              <h1 className="text-4xl font-bold text-gray-900 mb-3">
                Funds added successfully.
              </h1>

              <p className="text-gray-700 text-lg mb-8">
                We have successfully processed your deposit of {successDetails.addedAmount}. Your updated balance is now
                available for premium services and concierge requests.
              </p>
            </div>

            <div className="grid grid-cols-2 gap-8 mb-8">
              {/* Left Column */}
              <div>
                <div className="mb-8">
                  <p className="text-xs font-bold text-gray-600 uppercase mb-2">
                    New Balance
                  </p>
                  <p className="text-4xl font-bold text-green-700">
                    {successDetails.newBalance}
                  </p>
                  <p className="text-xs text-gray-600 mt-1">
                    Amount Added: {successDetails.addedAmount}
                  </p>
                  <p className="text-xs text-gray-600">
                    Transaction ID: {successDetails.transactionId}
                  </p>
                </div>

                <div className="flex gap-3">
                  <Button
                    onClick={() => navigate('/wallet')}
                    className="bg-green-700 hover:bg-green-800 text-white font-semibold px-6"
                  >
                    Done
                  </Button>
                  <button
                    onClick={() => navigate('/wallet/add-funds')}
                    className="px-6 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 font-semibold"
                  >
                    Add More Funds
                  </button>
                </div>
              </div>

            {/* Right Column - Security Message */}
            <Card className="border-2 border-green-200 bg-green-50">
              <CardContent className="p-6">
                <div className="text-center">
                  <Shield className="h-8 w-8 text-green-700 mx-auto mb-3" />
                  <p className="text-sm text-green-800">
                    <span className="font-bold">● MANAGED MARKETPLACE</span>
                    <br />
                    <br />
                    <span className="italic">
                      "Our team is currently verifying the underlying transaction nodes to
                      ensure your assets are<br /> fully secured within the Virewire
                      ecosystem."
                    </span>
                  </p>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Info Cards */}
          <div className="grid grid-cols-3 gap-4 mb-8">
            <Card className="border-1 bg-gray-50">
              <CardContent className="p-6 text-center">
                <Clock className="h-6 w-6 text-blue-600 mx-auto mb-2" />
                <p className="text-xs font-bold text-gray-600 uppercase mb-2">
                  Processing Time
                </p>
                <p className="font-bold text-gray-900">{successDetails.processingTime}</p>
              </CardContent>
            </Card>

            <Card className="border-1 bg-gray-50">
              <CardContent className="p-6 text-center">
                <Lock className="h-6 w-6 text-purple-600 mx-auto mb-2" />
                <p className="text-xs font-bold text-gray-600 uppercase mb-2">Security</p>
                <p className="font-bold text-gray-900">{successDetails.security}</p>
              </CardContent>
            </Card>

            <Card className="border-1 bg-gray-50">
              <CardContent className="p-6 text-center">
                <p className="emoji text-2xl mb-2">💬</p>
                <p className="text-xs font-bold text-gray-600 uppercase mb-2">
                  Need assistance?
                </p>
                <p className="font-bold text-gray-900">Our concierge is online</p>
              </CardContent>
            </Card>
          </div>
        </div>
        </div>
      )}
    </DashboardLayout>
  );
};

export default FundsAddedSuccessPage;
